import { Stack, Typography } from '@mui/material';
import { SxProps, useTheme } from '@mui/material/styles';
import { Checkbox } from 'common/form/components/Checkbox';
import { SearchPeopleProfile } from 'modules/sourcing-external/types/SearchPeopleQueryItem';
import { useSourcingExternalStore } from 'modules/sourcing-external/stores';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';

const containerSx: SxProps = {
  p: '20px',
  gap: '40px',
  flex: 1,
  cursor: 'pointer'
};

export const SourcingExternalCandidateCard = ({
  row,
  onCardClick
}: {
  row: SearchPeopleProfile;
  onCardClick: (row: SearchPeopleProfile) => void;
}) => {
  const candidateBatch = useSourcingExternalStore(
    (state) => state.candidateBatch
  );
  const selectedRows = useSourcingExternalStore((state) => state.selectedRows);
  const alreadyInvitedRows = useSourcingExternalStore(
    (state) => state.alreadyInvitedRows
  );
  const onSelectRow = useSourcingExternalStore((state) => state.onSelectRow);
  const onRemoveSelectedRow = useSourcingExternalStore(
    (state) => state.onRemoveSelectedRow
  );
  const pendingSelections = useSourcingExternalStore(
    (state) => state.pendingSelections
  );
  const { palette } = useTheme();
  const { track } = useTracking();

  const selectedRow = selectedRows.find(
    (selectedRow) => selectedRow.linkedInProfileUrl === row.linkedInProfileUrl
  );

  const isAlreadyInvited = alreadyInvitedRows.find(
    (alreadyInvitedRow) =>
      alreadyInvitedRow.linkedInProfileUrl === row.linkedInProfileUrl
  );

  const isPending = pendingSelections.has(row.linkedInProfileUrl);

  const handleSelectRow = (row: SearchPeopleProfile) => {
    if (!candidateBatch || isPending) return;

    if (selectedRow) {
      onRemoveSelectedRow(selectedRow);
    } else {
      track(TrackingEventName.SourcingSelectCandidateToInvite, {
        candidateBatchId: candidateBatch.id,
        candidateLinkedInProfileUrl: row.linkedInProfileUrl
      });
      onSelectRow(candidateBatch, row);
    }
  };

  return (
    <Stack
      key={row.linkedInProfileUrl}
      sx={{
        ...containerSx,
        // NOTE: This is to prevent the border from jumping
        margin: selectedRow ? '0px' : '2px',
        border: selectedRow
          ? `${palette.border.large.primary.main}`
          : `${palette.border.small.grey.medium}`,
        cursor: 'pointer'
      }}
      onClick={() => onCardClick(row)}
    >
      <Stack sx={{ gap: '15px' }}>
        <Typography
          variant="heading"
          sx={{
            color: 'primary.main'
          }}
        >
          {row.fullName}
        </Typography>
        <Typography variant="subtext">
          {row.experience?.slice(0, 3).map((exp, i) => (
            <li key={i} style={{ marginLeft: '14px' }}>
              {exp}
            </li>
          ))}
        </Typography>
      </Stack>
      <Stack sx={{ flex: 1, justifyContent: 'flex-end' }}>
        {isAlreadyInvited ? (
          <Typography variant="defaultEmphasis">Already invited</Typography>
        ) : (
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              value={!!selectedRow || isPending}
              onChange={() => handleSelectRow(row)}
              field={{
                checkboxLabel: 'Invite to apply',
                controlSx: {
                  color: 'primary.main'
                },
                disabled: isPending
              }}
            />
          </span>
        )}
      </Stack>
    </Stack>
  );
};
